import { useEffect, useRef } from 'react'

const W = 320
const NODES = [
  { id: 'user', label: 'User Voice', y: 40, color: '#1ec8ff' },
  { id: 'stt', label: 'STT', y: 110, color: '#2a8bff' },
  { id: 'llm', label: 'LLM', y: 180, color: '#8a55ff' },
  { id: 'tts', label: 'TTS', y: 250, color: '#ff5a8c' },
  { id: 'out', label: 'Voice Out', y: 320, color: '#39e0b0' },
]
const PACKETS_PER_LINK = 4

const BUDGET = [
  { name: 'STT', ms: 240, color: '#2a8bff' },
  { name: 'LLM', ms: 320, color: '#8a55ff' },
  { name: 'TTS', ms: 180, color: '#ff5a8c' },
  { name: 'net', ms: 60, color: '#5d7891' },
]
const TOTAL = BUDGET.reduce((s, b) => s + b.ms, 0)

// Bezier arc from top node to bottom node, bulging to the right — the
// speech-native path that skips the staged pipeline entirely.
function arcPoint(u) {
  const A = { x: 160, y: 40 }, C = { x: 30, y: 180 }, B = { x: 160, y: 320 }
  const x = (1 - u) * (1 - u) * A.x + 2 * (1 - u) * u * C.x + u * u * B.x
  const y = (1 - u) * (1 - u) * A.y + 2 * (1 - u) * u * C.y + u * u * B.y
  return { x, y }
}
const ARC_PATH = Array.from({ length: 40 }, (_, i) => arcPoint(i / 39))
const ARC_D = ARC_PATH.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')

// The classic realtime voice-agent loop (User → STT → LLM → TTS → Voice Out)
// with the speech-native bypass arc and its p95 latency budget.
export function Scene11Agents2D() {
  const arcDotRef = useRef()
  const packetRefs = useRef([])
  const raf = useRef(0)

  useEffect(() => {
    let start = performance.now()
    const loop = (now) => {
      const t = Math.max(0, (now - start) / 1000)

      const u = (t * 0.2) % 1
      const p = arcPoint(u)
      if (arcDotRef.current) {
        arcDotRef.current.setAttribute('cx', p.x.toFixed(1))
        arcDotRef.current.setAttribute('cy', p.y.toFixed(1))
      }

      NODES.slice(0, -1).forEach((n, li) => {
        const next = NODES[li + 1]
        for (let k = 0; k < PACKETS_PER_LINK; k++) {
          const phase = ((k / PACKETS_PER_LINK) + t * 0.35) % 1
          const el = packetRefs.current[li * PACKETS_PER_LINK + k]
          if (el) {
            el.setAttribute('cy', (n.y + 22 + phase * (next.y - n.y - 44)).toFixed(1))
          }
        }
      })

      raf.current = requestAnimationFrame(loop)
    }
    raf.current = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf.current)
  }, [])

  const barY = 380
  const barX0 = 30
  const barW = W - 60
  let x = barX0
  const segments = BUDGET.map((b) => {
    const w = (b.ms / TOTAL) * barW
    const seg = { ...b, w, x }
    x += w
    return seg
  })

  return (
    <svg viewBox={`0 0 ${W} 470`} style={{ width: '100%', maxWidth: 340, height: 'auto', display: 'block', margin: '0 auto' }}>
      <path d={ARC_D} fill="none" stroke="#9fe0ff" strokeWidth="1.5" strokeDasharray="5 4" opacity="0.6" />
      <circle ref={arcDotRef} r="4" fill="#dff4ff" />
      <text x="30" y="180" fill="#bfe6ff" fontSize="9" fontFamily="system-ui, sans-serif" textAnchor="middle" transform="rotate(-90, 30, 180)">speech-native · audio in → audio out</text>

      {NODES.slice(0, -1).map((n, i) => (
        <line key={i} x1={W / 2} y1={n.y} x2={W / 2} y2={NODES[i + 1].y} stroke={n.color} strokeWidth="1" opacity="0.25" />
      ))}
      {NODES.slice(0, -1).map((n, li) => (
        Array.from({ length: PACKETS_PER_LINK }).map((_, k) => (
          <circle key={`${li}-${k}`} ref={(el) => { packetRefs.current[li * PACKETS_PER_LINK + k] = el }}
            cx={W / 2} r="3" fill={NODES[li + 1].color} opacity="0.9" />
        ))
      ))}
      {NODES.map((n) => (
        <g key={n.id}>
          <circle cx={W / 2} cy={n.y} r="16" fill={n.color} />
          <text x={W / 2 + 26} y={n.y} fill={n.color} fontSize="12" fontFamily="system-ui, sans-serif" dominantBaseline="middle">{n.label}</text>
        </g>
      ))}

      {segments.map((s) => (
        <g key={s.name}>
          <rect x={s.x} y={barY} width={s.w - 1.5} height="18" fill={s.color} />
          <text x={s.x + s.w / 2} y={barY + 12} fill="#02060d" fontSize="8" fontFamily="system-ui, sans-serif" textAnchor="middle">{s.name}</text>
        </g>
      ))}
      <text x={W / 2} y={barY + 40} fill="#aee3ff" fontSize="12" fontFamily="system-ui, sans-serif" textAnchor="middle">≈ {TOTAL}ms p95</text>
      <text x={W / 2} y={barY + 58} fill="#5d7891" fontSize="9" fontFamily="monospace" textAnchor="middle" letterSpacing="0.05em">latency budget — every ms decides if it feels alive</text>
    </svg>
  )
}
